import React from "react";
import { Card, CardContent, Typography, Button, Box, Link, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const NewsCard = ({ news }) => {
  const theme = useTheme();
  const isMobile = !useMediaQuery(theme.breakpoints.up("md"));

  // Helper function to format the date
  const formatDate = (dateString) => {
    if (!dateString) return "Just now";
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <Card
      sx={{
        scrollSnapAlign: "start",
        height: { xs: "100vh", md: "300px" },
        width: {
          xs: "100%",
          sm: "100%",
          md: "70%"
        },
        margin: "0 auto",
        marginTop: { xs: 0, md: 3 },
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderRadius: 2,
        // boxShadow: { md: 3 },
        overflow: "hidden",
        userSelect: "none",
        WebkitUserSelect: "none",
        MozUserSelect: "none",
        msUserSelect: "none",
        WebkitTapHighlightColor: "transparent",
        "& *": {
          userSelect: "none",
          WebkitUserSelect: "none",
          MozUserSelect: "none",
          msUserSelect: "none"
        }
      }}
    >
      {/* Image Container */}
      <Box
        sx={{
          aspectRatio: "4:3",
          // overflow:"hidden",
          paddingTop:{xs:"56px",md:"0px"},
          flex: { xs: '0 0 10%', md: '0 0 320px' },
          width: { xs: '100%', md: '320px' },
          height: { xs: '100%', md: '100%' }, // Fixed height on mobile (reduced from 30%)
        }}
      >
        <img
          src={news.image_url} // Directly using the URL
          alt={news.title}
          draggable="false"
          onDragStart={(e) => e.preventDefault()}
          style={{
            aspectRatio:"16/9",
            // borderRadius:"20px",
            width: '100%',
            height: '100%',
            objectFit: "cover",
            display: "block",
            pointerEvents: "none",
            userSelect: "none"
          }}
        />
      </Box>

      {/* Content Container */}
      <CardContent sx={{
        padding: { xs: '12px 24px 80px 12px', md: 3 }, // Extra bottom padding on mobile
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        textAlign: 'left',
        overflow: 'hidden' // Prevents content from spilling out
      }}>
        <Box>
          <Typography
            // fontSize={"20px"}
            fontWeight="bold"
            variant="h6"
            gutterBottom
            component={isMobile ? "a" : "h2"}
            href={isMobile ? news.url : undefined}
            target={isMobile ? "_blank" : undefined}
            rel={isMobile ? "noopener noreferrer" : undefined}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: '4',
              WebkitBoxOrient: 'vertical',
              ...(isMobile && {
                cursor: 'pointer',
                textDecoration: 'none',
                color: 'inherit',
                '&:hover': {
                  textDecoration: 'underline'
                }
              })
            }}
          >
            {news.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Source: <Link href={`https://${news.source}`} target="_blank" rel="noopener">{news.source}</Link>
            {' / '}
            {formatDate(news.published_at)}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: 'left',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: '4',
              WebkitBoxOrient: 'vertical',
              mb: isMobile ? 0.5 : 2
            }}
          >
            {news.description || news.snippet || "No description available."}
          </Typography>
          {isMobile && (
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 500,
                mb: 2
              }}
            >
              ... Read more
            </Typography>
          )}
        </Box>

        {!isMobile && (
          <Box sx={{ mb: { xs: 4, md: 0 } }}>
            <Button
              variant="contained"
              href={news.url}
              target="_blank"
              sx={{
                mt: 1,
                width: { xs: '100%', md: 'auto' },
                alignSelf: { xs: 'stretch', md: 'flex-start' }
              }}
            >
              Read More
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default NewsCard;
